import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { Icon } from '@homeexchange/design';

import i18n from '../../i18n';

export default class EditableField extends React.PureComponent {
    static GUESTPOINT_AMOUNT = 'guestpoint_amount';

    static propTypes = {
        name: PropTypes.string.isRequired,
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
        children: PropTypes.node.isRequired,
        renderer: PropTypes.func.isRequired,
        extra: PropTypes.func,
        extraLabel: PropTypes.func,
        onChange: PropTypes.func.isRequired,
        editable: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]).isRequired,
        valueTransformer: PropTypes.func,
        validator: PropTypes.func,
        required: PropTypes.bool,
        exchangeIsApproved: PropTypes.bool
    };

    getInitialValue(exchangeIsApproved, inputName, inputValue) {
        if (exchangeIsApproved && inputName === 'guest_nb' && !this.props.editable) {
            return inputValue;
        }

        return inputValue ? inputValue : '';
    }

    static defaultProps = {
        valueTransformer: (val) => val,
        required: true
    };

    constructor(props) {
        super(props);

        this.state = {
            prevValue: this.props.value ? this.props.value : null,
            value: this.getInitialValue(this.props.exchangeIsApproved, this.props.name, this.props.value),
            validity: true,
            editing: false
        };

        this.input = React.createRef();

        this.edit = this.edit.bind(this);
        this.onEdit = this.onEdit.bind(this);
        this.save = this.save.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.toggleEditGuestPoints = this.toggleEditGuestPoints.bind(this);
        this.updateGPPerNight = this.updateGPPerNight.bind(this);
        this.handleGPRowClick = this.handleGPRowClick.bind(this);
    }

    componentDidMount() {
        // eslint-disable-next-line react/no-did-mount-set-state
        this.setState({ validity: this.checkValidity() });
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!this.state.editing) {
            this.setState(() => ({
                value: this.getInitialValue(nextProps.exchangeIsApproved, nextProps.name, nextProps.value),
                prevValue: nextProps.value
            }));
        }
    }

    getInput() {
        return $(this.input.current).find('input, select, textarea');
    }

    showInput() {
        this.setState({
            editing: true
        });
    }

    hideInput() {
        this.setState({
            editing: false
        });
    }

    edit() {
        this.showInput();
        this.onEdit();
    }

    toggleEditGuestPoints(e) {
        e.stopPropagation();
        const { editing, prevValue } = this.state;
        if (editing) {
            this.setState({ editing: !editing, value: prevValue });
        } else {
            this.setState({ editing: !editing });
        }
    }

    handleGPRowClick() {
        this.setState({ editing: true });
    }

    onEdit() {
        const $input = this.getInput();
        $input.focus();
        $input.on('blur', this.save);
    }

    save() {
        this.getInput().off('blur');
        if (this.onSave()) {
            this.hideInput();
        }
    }

    updateGPPerNight() {
        this.setState(
            {
                validity: true
            },
            () => {
                this.props.onChange(EditableField.GUESTPOINT_AMOUNT, this.state.value);
                this.setState({ editing: false });
            }
        );
    }

    onSave() {
        const $input = this.getInput();
        let value = $input.val();
        const validity = this.checkValidity();
        if ((this.props.required && value === '') || !validity) {
            return false;
        }
        value = this.props.valueTransformer(value);
        this.setState(
            {
                value,
                validity
            },
            () => {
                this.props.onChange(this.props.name, this.state.value);
            }
        );
        $input.blur();
        return true;
    }

    onKeyDown(e) {
        if (e.which == 13 || e.keyCode == 13) {
            // stop propagation to prevent confirm popup to ba validated in same time
            e.preventDefault();
            e.stopPropagation();
            this.save();
        }
    }

    isValid() {
        return (
            this.state.value !== null &&
            this.state.value !== undefined &&
            this.state.validity &&
            (!this.props.validator || Boolean(this.props.validator(this.state.value)))
        );
    }

    checkValidity() {
        const $input = this.getInput();
        if ($input.length > 0) {
            return $input[0].checkValidity();
        }
        return false;
    }

    isEditable() {
        if (this.props.editable && typeof this.props.editable === 'function') {
            return this.props.editable();
        } else {
            return Boolean(this.props.editable);
        }
    }

    handleClick(e) {
        if ($(e.target).closest('.btn-link').length === 0) {
            this.edit();
        }
    }

    render() {
        return (
            <div
                className={classNames('form-group editable-field', this.props.name, {
                    editable: this.isEditable()
                })}
                onClick={
                    this.props.name === EditableField.GUESTPOINT_AMOUNT
                        ? this.handleGPRowClick
                        : this.handleClick
                }
            >
                {this.renderLabel()}
                {this.renderContent(this.props.name)}
                {(() => {
                    if (this.props.extra) {
                        return this.props.extra();
                    }
                })()}
            </div>
        );
    }

    renderLabel() {
        if (this.props.label) {
            return (
                <span>
                    <label htmlFor={this.props.name}>{this.props.label}</label>
                    {this.props.extraLabel ? this.props.extraLabel() : null}
                </span>
            );
        }
        return false;
    }

    renderContent(name = null) {
        if (this.isEditable()) {
            if (name === EditableField.GUESTPOINT_AMOUNT) {
                return (
                    <>
                        <div className="editable-content">
                            <span
                                className="value"
                                dangerouslySetInnerHTML={{
                                    __html: this.props.renderer(this.state.value)
                                }}
                            />
                            {this.renderButtons(this.props.name)}
                        </div>
                        {this.state.editing && this.renderInput(name)}
                    </>
                );
            }

            return (
                <div
                    className={classNames('editable-content', {
                        empty: !this.isValid(),
                        editing: this.state.editing
                    })}
                >
                    <span
                        className={classNames('value', {
                            hidden: this.state.editing
                        })}
                        dangerouslySetInnerHTML={{
                            __html: this.props.renderer(this.state.value)
                        }}
                    />
                    <span
                        className={classNames('input', {
                            hidden: !this.state.editing
                        })}
                        ref={this.input}
                    >
                        {this.renderInput(name)}
                    </span>
                    {this.renderButtons(this.props.name)}
                </div>
            );
        } else {
            return (
                <span
                    className="value"
                    dangerouslySetInnerHTML={{
                        __html: this.props.renderer(this.state.value)
                    }}
                />
            );
        }
    }

    renderInput(name = null) {
        let props = {
            value: this.state.value,
            onChange: (e) => this.setState({ value: e.target.value }),
            onKeyDown: this.onKeyDown,
            ...(name === EditableField.GUESTPOINT_AMOUNT && { save: this.updateGPPerNight })
        };
        return React.Children.map(this.props.children, (child) => {
            // trigger save on change if input is select or radio
            if (child.type === 'select' || child.type === 'radio') {
                props = Object.assign(props, {
                    onChange: this.save
                });
            }
            return React.cloneElement(child, props);
        });
    }

    renderButtons(name = null) {
        return (
            <span className="buttons">
                {(() => {
                    if (name === EditableField.GUESTPOINT_AMOUNT) {
                        return (
                            <button className="btn-link btn-edit" onClick={this.toggleEditGuestPoints}>
                                <Icon
                                    name="Pencil"
                                    title={i18n.t('common:edit')}
                                    size="large"
                                    aria-hidden="true"
                                />
                                <span className="sr-only">{i18n.t('common:edit')}</span>
                            </button>
                        );
                    } else {
                        return (
                            <>
                                <button
                                    className={classNames('btn-link btn-edit', {
                                        hidden: this.state.editing
                                    })}
                                    onClick={this.edit}
                                >
                                    <Icon
                                        name="Pencil"
                                        size="large"
                                        title={i18n.t('common:edit')}
                                        aria-hidden="true"
                                    />
                                    <span className="sr-only">{i18n.t('common:edit')}</span>
                                </button>
                                <button
                                    className={classNames('btn-link btn-save', {
                                        hidden: !this.state.editing
                                    })}
                                    onClick={this.save}
                                >
                                    <Icon
                                        name="Check"
                                        size="large"
                                        title={i18n.t('common:save')}
                                        aria-hidden="true"
                                    />
                                    <span className="sr-only">{i18n.t('common:save')}</span>
                                </button>
                            </>
                        );
                    }
                })()}
            </span>
        );
    }
}
